import { PlusIcon } from '@heroicons/react/24/outline'
import { Form, Formik, getIn, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import * as Yup from 'yup'

import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from 'constants/constants'
import { DE, getAllLanguages } from '../../constants/languages'
import trackEvents from '../../constants/track-events'
import {
  useNotificationContext,
  usePartnerContext,
  usePaymentContext,
  useUserContext
} from '../../context'
import { MODES } from '../../features/pricing/entitlements'
import { useEntitlements } from '../../features/pricing/hooks'
import { isVersionedCareerCopy } from '../../features/pricing/usage/careers'
import { query } from '../../graphql'
import {
  EMAIL_REGEX,
  classNames,
  sendSlackMessage,
  uuidSmall
} from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import {
  inviteOutcome,
  mailNotSentSpaceId,
  newlyCreatedSpaces,
  planRetry
} from '../../helpers/invite-outcomes'
import {
  MAX_RECIPIENTS,
  allRecipients,
  syncRecipientNames
} from '../../helpers/invite-recipients'
import { runWithConcurrency } from '../../helpers/run-with-concurrency'
import { isGeneratedSampleCareer } from '../../helpers/sample-data'
import { useMailTemplates } from '../../hooks/use-mail-templates'
import { useMutationAndUpdateQuery } from '../../hooks/use-mutation-and-update-query'
import {
  LIFE_CYCLE_STAGES,
  useLifecycleStageTracking
} from '../../hooks/useLifecycleStageTracking'
import EmailChipsInput from '../email-chips-input'
import Input from '../input'
import EditMailTemplate from '../mail-template/edit-mail-template'
import { StepTransition } from '../motion'
import SelectMenu from '../select-menu'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'
import InviteBatchSuccess from './invite-batch-success'
import InviteSuccess from './invite-success'
import TalentStorageInviteHint from './talent-storage-invite-hint'

const TRANSLATION_LOCATION = 'components.career.invite_talent_modal.'

// Sprachauswahl für Einladungen in den Talentpool, seit den Mail-Templates
// (#456) ausgeblendet. Die Sprache bleibt beim Default DE.
const SHOW_TALENTPOOL_LANGUAGE_SELECT = false

const INITIAL_VALUES = {
  // getippte Adresse, die noch kein Chip ist
  email: '',
  // Chips im E-Mail-Feld, je { email, firstname, lastname }
  recipients: [],
  partner_user_id: '',
  firstname: '',
  lastname: ''
}

const FORM_ID = 'invite-talent-form'

// inviteTalent lädt ein Talent pro Aufruf, so viele laufen gleichzeitig
const INVITE_CONCURRENCY = 3

// Reihenfolge, in der nach einem Absenden mit Fehlern fokussiert wird.
// recipients sind die Namenszeilen je Adresse im Mehrfach-Modus.
const FIELD_ORDER = [
  'email',
  'recipients',
  'firstname',
  'lastname',
  'partner_user_id'
]

const CHIP_PROBLEMS = {
  invalid: 'chip_invalid',
  invited: 'chip_invited',
  too_many: 'chip_too_many'
}

// Felder mit Fehler in Formularreihenfolge, als name im DOM
const errorFieldNames = (errors) =>
  FIELD_ORDER.flatMap((id) => {
    if (id !== 'recipients') return errors[id] ? [id] : []

    const rows = Array.isArray(errors.recipients) ? errors.recipients : []
    return rows.flatMap((row, index) =>
      ['firstname', 'lastname']
        .filter((key) => row?.[key])
        .map((key) => `recipients.${index}.${key}`)
    )
  })

// Nach einem Absenden mit Fehlern springt der Fokus ins erste fehlerhafte
// Feld, statt dass der Button grau bleibt und keiner weiß warum
const FocusFirstError = () => {
  const { submitCount, isSubmitting, errors, values, setValues } =
    useFormikContext()
  const handledSubmitCount = useRef(0)

  useEffect(() => {
    if (isSubmitting || submitCount === handledSubmitCount.current) return
    handledSubmitCount.current = submitCount

    const [field] = errorFieldNames(errors)
    if (!field) return

    const focus = () =>
      document.querySelector(`#${FORM_ID} [name="${field}"]`)?.focus()

    // Bei mehreren Adressen wird die getippte zum Chip, damit Markierung und
    // Namenszeile an ihr hängen. Fokussiert wird dann nach dem Rendern.
    if (values.email.trim() && allRecipients(values).length > 1) {
      setValues({ ...values, recipients: allRecipients(values), email: '' })
      setTimeout(focus, 0)
      return
    }

    focus()
  }, [submitCount, isSubmitting, errors, values, setValues])

  return null
}

// Die Talent-ID braucht man fast nur für die Zuordnung im ATS. Sie bleibt
// eingeklappt, außer sie ist schon belegt oder fehlerhaft.
const TalentIdField = ({ disabled }) => {
  const { t } = useTranslation()
  const { values, errors, touched, handleChange, handleBlur } =
    useFormikContext()
  const [isOpen, setIsOpen] = useState(!!values.partner_user_id)
  const [openedByUser, setOpenedByUser] = useState(false)

  const hasError = touched.partner_user_id && !!errors.partner_user_id

  if (!isOpen && !hasError) {
    return (
      <div className='mt-4'>
        <Button.Text
          minWidth=''
          icon={<PlusIcon className='size-4' aria-hidden='true' />}
          text={t(TRANSLATION_LOCATION + 'add_talent_id_action')}
          onClick={() => {
            setIsOpen(true)
            setOpenedByUser(true)
          }}
        />
      </div>
    )
  }

  return (
    <div className='mt-6'>
      <Input
        id='partner_user_id'
        label={t(TRANSLATION_LOCATION + 'partner_user_id_label')}
        description={t(TRANSLATION_LOCATION + 'partner_user_id_description')}
        optional
        inlineErrorMessage={false}
        value={values.partner_user_id}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.partner_user_id}
        error={errors.partner_user_id}
        autoComplete='off'
        autoFocus={openedByUser}
        disabled={disabled}
      />
    </div>
  )
}

TalentIdField.propTypes = {
  disabled: PropTypes.bool.isRequired
}

const NAME_INPUT_CLASSES =
  'block w-full rounded-md text-sm shadow-sm placeholder:italic'

// Nutzt die Vorlage den Namen, braucht bei mehreren Adressen jede einen.
// Namen, die beim Einfügen mitkamen, stehen schon drin. Markierte Adressen
// fehlen, sie gehen so nicht raus.
const RecipientNames = ({ problemOf, disabled }) => {
  const { t } = useTranslation()
  const { values, errors, touched, submitCount, handleChange, handleBlur } =
    useFormikContext()

  return (
    <div className='mt-6'>
      <h3 className='text-sm font-medium text-gray-700'>
        {t(TRANSLATION_LOCATION + 'recipient_names_title')}
      </h3>
      <p className='mt-1 text-xs text-gray-500'>
        {t(TRANSLATION_LOCATION + 'name_required_hint')}
      </p>
      <ul className='mt-3 max-h-80 space-y-4 overflow-y-auto'>
        {values.recipients.map((recipient, index) => {
          if (problemOf(recipient.email, index)) return null

          return (
            <li key={recipient.email}>
              <p className='truncate text-xs font-medium text-gray-700'>
                {recipient.email}
              </p>
              <div className='mt-1 grid grid-cols-2 gap-x-3'>
                {['firstname', 'lastname'].map((key) => {
                  const path = `recipients[${index}].${key}`
                  const error = getIn(errors, path)
                  const showError =
                    !!error && (submitCount > 0 || !!getIn(touched, path))

                  return (
                    <div key={key}>
                      <input
                        name={`recipients.${index}.${key}`}
                        type='text'
                        value={recipient[key]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t(TRANSLATION_LOCATION + `${key}_label`)}
                        aria-label={t(
                          TRANSLATION_LOCATION + `recipient_${key}_label`,
                          { email: recipient.email }
                        )}
                        aria-invalid={showError || undefined}
                        autoComplete='off'
                        disabled={disabled}
                        className={classNames(
                          NAME_INPUT_CLASSES,
                          showError
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                        )}
                      />
                      {showError && (
                        <p className='mt-1 text-xs text-red-600'>{error}</p>
                      )}
                    </div>
                  )
                })}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

RecipientNames.propTypes = {
  // (email, index) => Problem-Schlüssel oder null
  problemOf: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired
}

const CreateSpaceModal = ({
  data,
  openCreateSpaceModal,
  closeCreateSpaceModal,
  open,
  invitationType,
  refetchSpaces,
  onInvited
}) => {
  const { t } = useTranslation()
  const { is_trial } = usePaymentContext()
  const { partner } = usePartnerContext()
  const { error } = useNotificationContext()
  const {
    cognitoUser: { username }
  } = useUserContext()
  const { trackLifecycleStage } = useLifecycleStageTracking()

  // Versionierte Altkopien (<ext>-p-<pid>-v<n>) zählen nicht als Stelle und
  // sind deshalb mit Pricing-Sperre nicht mehr einladbar. Bestehende Links und
  // Talente der Kopie laufen weiter. Ohne Sperre bleibt die Liste wie heute.
  const hideVersionedCopies = useEntitlements().mode === MODES.ENFORCED

  const { spaces, careers, careers_map } = data

  const { state: locationState } = useLocation()
  const queryClient = useQueryClient()

  const [state, setState] = useState('INITIAL')

  const context = invitationType

  const [uuid, setUuid] = useState()
  const [showEditMailTemplate, setShowEditMailTemplate] = useState(false)
  const [space, setSpace] = useState(null)
  const [invitee, setInvitee] = useState({})
  // Talent angelegt, aber die Einladungs-Mail ging nicht raus (MAIL_NOT_SENT)
  const [mailNotSent, setMailNotSent] = useState(false)
  const [isResending, setIsResending] = useState(false)
  // Ergebnis der Mehrfach-Einladung je Adresse, siehe inviteOutcome
  const [outcomes, setOutcomes] = useState([])
  // { done, total }, solange mehrere Einladungen rausgehen
  const [progress, setProgress] = useState(null)

  // Talente dieser Modal-Sitzung, die Tabelle hebt sie nach dem Schließen hervor
  const invitedSpaceIds = useRef([])
  // Beim ersten Talent wechselt talents.js nach dem Refetch vom Empty-State in
  // die Tabelle und rendert dabei eine andere Modal-Instanz. Deshalb erst beim
  // Schließen refetchen, sonst verschwindet der Erfolgs-Step samt Link.
  const refetchOnClose = useRef(false)
  // Der Editor der Vorlage ist ein eigenes Modal. Das Einladen-Modal schließt
  // dafür kurz und baut das Formular ab, der Entwurf überlebt hier. Die
  // Vorschau liegt dagegen über dem Einladen-Modal.
  const formikRef = useRef(null)
  const draftValues = useRef(null)
  const [mailPreview, setMailPreview] = useState({
    open: false,
    recipient: undefined
  })

  const [language, setLanguage] = useState(DE)
  const [careerID, setCareerID] = useState('-1')

  useEffect(() => {
    if (context === 'EMAIL') return

    setUuid(uuidSmall())
  }, [context])

  const careerMutation = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.talentsByPartner(partner.id, username),
    mutation: 'updateCareer',
    updater: ({ prevData, response }) => {
      const newData = [...prevData]

      // queryFn: () => Promise.all([getAllSpaces(), getCareers()])
      // [0] = spaces / [1] = careers
      newData[1] = [
        ...prevData[1].filter(({ id }) => id !== response.id),
        response
      ]

      return newData
    }
  })

  const partnerMutation = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.partnerById(partner.id),
    mutation: 'updatePartner'
  })

  const selectedCareer = careers_map.get(careerID)

  const { mailTemplate, mailTemplateMacros } = useMailTemplates({
    career: selectedCareer
  })

  const editMailTemplateProps = selectedCareer
    ? {
        mailTemplate,
        mailTemplateMacros,
        // app_settings immer spreaden, nie im Cache-Objekt ändern. Sonst steht
        // die neue Vorlage dort, auch wenn das Speichern fehlschlägt.
        handleSubmit: ({ result, onSuccess, onError }) => {
          const app_settings = {
            ...selectedCareer.app_settings,
            mail_template: result
          }

          const input = { id: selectedCareer.id, app_settings }
          careerMutation.mutate({ input }, { onSuccess, onError })
        }
      }
    : {
        mailTemplate,
        mailTemplateMacros,
        handleSubmit: ({ result, onSuccess, onError }) => {
          const app_settings = {
            ...partner.app_settings,
            mail_template: result
          }

          const input = { id: partner.id, app_settings }
          partnerMutation.mutate({ input }, { onSuccess, onError })
        }
      }

  const trackFirstTalentInvited = () => {
    const storageKey = 'firstTalentInvitedTracked'
    if (localStorage.getItem(storageKey) === 'true') return

    trackLifecycleStage({
      lifecycle_stage: LIFE_CYCLE_STAGES.FIRST_TALENT_INVITED,
      lifecycle_stage_before: LIFE_CYCLE_STAGES.ONBOARDING_COMPLETED,
      aivy_form_id: partner.aivy_form_id,
      lead_progress: (6 / 5).toFixed(2),
      partner_id: partner.id
    })
    localStorage.setItem(storageKey, 'true')
  }

  // Slack und Tracking. Mehrere Einladungen melden sich mit einer
  // Slack-Nachricht pro Durchgang, das Event geht weiter je Talent, damit
  // Auswertungen gleich zählen.
  const reportInvites = ({ count, viaEmail, batchSize }) => {
    const type = viaEmail ? 'email' : 'link'
    const via = viaEmail ? 'via E-Mail' : 'via Link'
    const talents = count > 1 ? `${count} Talente` : 'einen Talent'

    for (let i = 0; i < count; i += 1) {
      trackEvent(
        selectedCareer
          ? trackEvents.INVITE_TALENT_CAREER
          : trackEvents.INVITE_TALENT_POOL,
        batchSize ? { type, batch_size: batchSize } : { type }
      )
    }

    sendSlackMessage(
      'kunden-controlling',
      selectedCareer
        ? `${partner.display_name} hat soeben ${talents} zur Stelle ${selectedCareer.title} eingeladen (${via}).`
        : `${partner.display_name} hat soeben ${talents} ohne Stellenbezug eingeladen (${via}).`
    )
  }

  // Was nach erfolgreichen Einladungen passiert, für eine oder mehrere
  const afterInvites = async ({ invitedSpaces, viaEmail, batchSize }) => {
    trackFirstTalentInvited()

    if (is_trial) {
      await queryClient.refetchQueries({
        queryKey: QUERY_KEYS.trialDataByPartner(partner.id),
        type: 'active'
      })
    }

    reportInvites({ count: invitedSpaces.length, viaEmail, batchSize })
    invitedSpaceIds.current.push(...invitedSpaces.map(({ id }) => id))

    if (spaces.length) refetchSpaces()
    else refetchOnClose.current = true
  }

  // Variablen für inviteTalent per E-Mail an eine Adresse. Steht das Talent
  // schon (MAIL_NOT_SENT), schickt space_id nur die Mail nach.
  const emailInviteInput = ({ email, firstname, lastname, unsentSpaceId }) => ({
    send_mail: true,
    email_template: 'invite',
    space_id: unsentSpaceId || undefined,
    partner_id: partner.id,
    email,
    firstname: firstname?.trim() || undefined,
    lastname: lastname?.trim() || undefined,
    language: selectedCareer?.language || language,
    career_id: selectedCareer?.id
  })

  // Schickt Einladungen an mehrere Adressen, höchstens INVITE_CONCURRENCY
  // gleichzeitig. Ein Fehler hält die übrigen nicht auf.
  const sendInvites = async (recipients) => {
    const total = recipients.length
    setProgress({ done: 0, total })

    try {
      const results = await runWithConcurrency(
        recipients,
        INVITE_CONCURRENCY,
        (recipient) =>
          query({
            query: 'inviteTalent',
            variables: emailInviteInput(recipient)
          }),
        { onSettled: (done) => setProgress({ done, total }) }
      )

      const result = recipients.map((recipient, index) =>
        inviteOutcome(recipient, results[index])
      )

      // Tracking und Refetch dürfen das Ergebnis nicht verdecken, die Talente
      // sind dann schon angelegt
      const invitedSpaces = newlyCreatedSpaces(recipients, result)
      if (invitedSpaces.length) {
        await afterInvites({
          invitedSpaces,
          viaEmail: true,
          batchSize: total
        }).catch((err) => console.error(err))
      }

      return result
    } finally {
      setProgress(null)
    }
  }

  async function inviteRecipients({ recipients, resetForm }) {
    setState('IS_LOADING')

    const result = await sendInvites(recipients)

    // Wurde nichts angelegt, bleibt das Formular samt Chips stehen. Talente
    // ohne Mail zeigt die Ergebnisliste, dort gehen sie nach.
    if (!result.some(({ space, unsentSpaceId }) => space || unsentSpaceId)) {
      setState('INITIAL')

      const notAuthorized = result.every(
        ({ error: reason }) => reason?.message === 'NOT_AUTHORIZED'
      )
      if (!notAuthorized) {
        error(t(TRANSLATION_LOCATION + 'invite_talents_email_error'))
      }
      return
    }

    setOutcomes(result)
    setState('SUCCESS_BATCH')

    draftValues.current = null
    resetForm()
  }

  // inviteTalent, nur ist MAIL_NOT_SENT hier kein Fehler. Talent und Person
  // stehen dann, es fehlt nur die Mail, und vom Space gibt es nur die ID.
  const inviteKeepingUnsent = async (variables) => {
    try {
      return { space: await query({ query: 'inviteTalent', variables }) }
    } catch (err) {
      const unsentSpaceId = mailNotSentSpaceId(err)
      if (!unsentSpaceId) throw err

      return {
        space: { id: unsentSpaceId, language: variables.language },
        mailNotSent: true
      }
    }
  }

  async function inviteTalent({ values, setFieldValue, resetForm }) {
    const recipients = allRecipients(values)

    if (context === 'EMAIL' && recipients.length > 1) {
      await inviteRecipients({ recipients, resetForm })
      return
    }

    setState('IS_LOADING')

    // oneOf([
    //  'CAREER_VIA_EMAIL', 'CAREER_VIA_LINK',
    //  'TALENTPOOL_VIA_EMAIL', 'TALENTPOOL_VIA_LINK',
    // ])
    const INVITE_STATE = `${
      selectedCareer ? 'CAREER' : 'TALENTPOOL'
    }_VIA_${context}`

    if (
      (INVITE_STATE === 'CAREER_VIA_LINK' ||
        INVITE_STATE === 'TALENTPOOL_VIA_LINK') &&
      !values.partner_user_id
    ) {
      await setFieldValue('partner_user_id', uuid)
      values.partner_user_id = uuid
    }

    const input = {}

    input.send_mail = false
    input.partner_id = partner.id
    input.partner_user_id = values.partner_user_id || undefined
    input.firstname = values.firstname || undefined
    input.lastname = values.lastname || undefined

    // defaults to DE from language state
    // could be set from the career
    // or via talentpool invitation
    input.language = language

    if (
      INVITE_STATE === 'CAREER_VIA_EMAIL' ||
      INVITE_STATE === 'TALENTPOOL_VIA_EMAIL'
    ) {
      input.send_mail = true
      input.email_template = 'invite'
      input.email = recipients[0].email
    }

    if (INVITE_STATE === 'CAREER_VIA_EMAIL') {
      const { id, language: career_language } = selectedCareer

      input.career_id = id
      if (career_language) input.language = career_language
    }

    if (INVITE_STATE === 'CAREER_VIA_LINK') {
      const { id, language: career_language } = selectedCareer

      input.career_id = id
      if (career_language) input.language = career_language
    }

    try {
      const { space, mailNotSent: unsent = false } =
        await inviteKeepingUnsent(input)
      setSpace(space)
      setMailNotSent(unsent)
      setUuid(uuidSmall())

      const viaEmail = INVITE_STATE.includes('EMAIL')
      await afterInvites({ invitedSpaces: [space], viaEmail })

      setInvitee({
        firstname: input.firstname,
        lastname: input.lastname,
        email: input.email
      })

      setState(viaEmail ? 'SUCCESS_VIA_EMAIL' : 'SUCCESS_VIA_LINK')

      draftValues.current = null
      resetForm()
    } catch (err) {
      setState('INITIAL')

      if (err.message === 'NOT_AUTHORIZED') return

      error(
        t(
          TRANSLATION_LOCATION +
            (INVITE_STATE.includes('EMAIL')
              ? 'invite_talent_email_error'
              : 'invite_talent_link_error')
        )
      )
    }
  }

  // Schon vergebene Adressen und Talent-IDs, damit das Feld es direkt sagt
  // statt eines Toasts nach dem Absenden
  const spaceByEmail = new Map(
    spaces
      .map((space) => [
        (space.person?.email || space.person?.invitation_email || '')
          .trim()
          .toLowerCase(),
        space
      ])
      .filter(([email]) => email)
  )
  const usedTalentIds = new Set(
    spaces
      .flatMap(({ partner_user_id, identification }) => [
        partner_user_id,
        identification
      ])
      .filter(Boolean)
  )

  // Gescheiterte noch einmal schicken. Talente ohne Mail bekommen sie über
  // space_id nach, der Rest siehe planRetry.
  const retryFailed = async () => {
    const { arrived, resend } = planRetry(outcomes, spaceByEmail)

    invitedSpaceIds.current.push(...[...arrived.values()].map(({ id }) => id))

    const resent = resend.length ? await sendInvites(resend) : []
    const resentByEmail = new Map(resent.map((o) => [o.email, o]))

    setOutcomes((current) =>
      current.map((outcome) => {
        if (outcome.space) return outcome
        if (arrived.has(outcome.email)) {
          return { ...outcome, space: arrived.get(outcome.email), error: null }
        }
        return resentByEmail.get(outcome.email) || outcome
      })
    )
  }

  // Schickt die Einladungs-Mail für das eben angelegte Talent über space_id
  // nach. Klappt es, zeigt der Erfolgs-Step den vollen Space mit Frist.
  const resendMail = async () => {
    setIsResending(true)

    try {
      const resent = await query({
        query: 'inviteTalent',
        variables: emailInviteInput({ ...invitee, unsentSpaceId: space.id })
      })
      setSpace((current) => resent || current)
      setMailNotSent(false)
    } catch (err) {
      if (err.message !== 'NOT_AUTHORIZED') {
        error(t(TRANSLATION_LOCATION + 'resend_email_error'))
      }
    } finally {
      setIsResending(false)
    }
  }

  // Während Einladungen rausgehen, bleibt das Modal offen. Sonst käme das
  // Ergebnis in einem geschlossenen Modal an und erschiene beim nächsten Öffnen.
  const isBusy = state === 'IS_LOADING' || !!progress || isResending

  // skipSetState heißt, das Modal schließt nur für den Vorlagen-Editor und
  // öffnet danach wieder. Refetch und Hervorheben warten dann aufs echte Ende.
  const handleClose = ({ skipSetState = false }) => {
    if (isBusy) return

    closeCreateSpaceModal()

    if (skipSetState) return

    draftValues.current = null

    if (refetchOnClose.current) {
      refetchOnClose.current = false
      refetchSpaces()
    }

    if (invitedSpaceIds.current.length) {
      onInvited(invitedSpaceIds.current)
      invitedSpaceIds.current = []
    }

    setTimeout(() => setState('INITIAL'), 1000 * 0.8)
  }

  const isSuccess = [
    'SUCCESS_VIA_EMAIL',
    'SUCCESS_VIA_LINK',
    'SUCCESS_BATCH'
  ].includes(state)

  // Die Namenspflicht kommt aus der Mailvorlage, beim Link gibt es keine Mail
  const isFirstnameRequired =
    context === 'EMAIL' && mailTemplate.has_firstname_macro
  const isLastnameRequired =
    context === 'EMAIL' && mailTemplate.has_lastname_macro

  // Was an einer Adresse nicht passt. Ab MAX_RECIPIENTS ist jede weitere zu
  // viel, es zählt die Reihenfolge im Feld.
  const problemOf = (email, index) => {
    if (!EMAIL_REGEX.test(email)) return 'invalid'
    if (spaceByEmail.has(email)) return 'invited'
    if (index >= MAX_RECIPIENTS) return 'too_many'
    return null
  }

  const isMultiple = (values) =>
    context === 'EMAIL' && allRecipients(values).length > 1

  const requiredMessage = t(TRANSLATION_LOCATION + 'validation_required')

  // Meldung unter dem Adressfeld. Eine Adresse wie bisher, bei mehreren eine
  // Zusammenfassung der markierten Chips.
  const recipientsError = (recipients) => {
    if (!recipients.length) return requiredMessage

    const problems = recipients.map(({ email }, index) =>
      problemOf(email, index)
    )

    if (recipients.length === 1) {
      if (problems[0] === 'invalid') {
        return t(TRANSLATION_LOCATION + 'validation_email_error')
      }
      if (problems[0] === 'invited') {
        return t(TRANSLATION_LOCATION + 'email_already_invited_error')
      }
      return null
    }

    const countOf = (problem) => problems.filter((p) => p === problem).length

    return (
      [
        ['invalid', 'recipients_invalid'],
        ['invited', 'recipients_invited'],
        ['too_many', 'recipients_too_many']
      ]
        .filter(([problem]) => countOf(problem))
        .map(([problem, key]) =>
          t(TRANSLATION_LOCATION + key, {
            count: countOf(problem),
            max: MAX_RECIPIENTS
          })
        )
        .join(' ') || null
    )
  }

  // Name und Talent-ID gelten nur für eine einzelne Adresse
  const whenSingle = (schema) =>
    Yup.string().when(['email', 'recipients'], ([email, recipients], base) =>
      isMultiple({ email, recipients }) ? base : schema
    )

  const validationSchema = Yup.object({
    ...(context === 'EMAIL' && {
      email: Yup.string().test('recipients', '', function () {
        const message = recipientsError(allRecipients(this.parent))
        return message ? this.createError({ message }) : true
      }),
      // Nutzt die Vorlage den Namen, braucht ihn bei mehreren jede Adresse
      recipients: Yup.array().test('recipient-names', '', function () {
        if (!isMultiple(this.parent)) return true

        const missing = allRecipients(this.parent).flatMap(
          (recipient, index) => {
            if (problemOf(recipient.email, index)) return []

            // Formik macht vor der Prüfung aus '' undefined
            return [
              isFirstnameRequired &&
                !recipient.firstname?.trim() &&
                'firstname',
              isLastnameRequired && !recipient.lastname?.trim() && 'lastname'
            ]
              .filter(Boolean)
              .map((key) =>
                this.createError({
                  path: `recipients[${index}].${key}`,
                  message: requiredMessage
                })
              )
          }
        )

        return missing.length ? new Yup.ValidationError(missing) : true
      })
    }),
    firstname: whenSingle(
      isFirstnameRequired
        ? Yup.string().trim().required(requiredMessage)
        : Yup.string()
    ),
    lastname: whenSingle(
      isLastnameRequired
        ? Yup.string().trim().required(requiredMessage)
        : Yup.string()
    ),
    // ohne g-Flag, sonst wechselt test() bei jedem Aufruf das Ergebnis
    partner_user_id: whenSingle(
      Yup.string()
        .matches(
          /^[a-zA-Z0-9-]*$/,
          t(TRANSLATION_LOCATION + 'partner_user_id_validation_error')
        )
        .test('talent-id-unused', '', function (value) {
          if (!value || !usedTalentIds.has(value)) return true

          return this.createError({
            message: t(TRANSLATION_LOCATION + 'partner_user_id_error', {
              talent_id: value
            })
          })
        })
    )
  })

  const careerOptions = [
    ['-1', t('talents.talentpool_title')],
    ...careers
      .filter(({ archived }) => !archived)
      // generierte Beispielstellen nicht, Discover-Stellen schon
      .filter((career) => !isGeneratedSampleCareer(career))
      .filter(
        (career) => !hideVersionedCopies || !isVersionedCareerCopy(career)
      )
      .map(({ id, title, external_custom_id }) => [
        id,
        title,
        external_custom_id && `| ${external_custom_id}`
      ])
  ]

  const openMailTemplateDialog = (setShow) => {
    if (isBusy) return

    draftValues.current = formikRef.current?.values || null
    handleClose({ skipSetState: true })
    setTimeout(() => setShow(true), 1000 * 0.4)
  }

  const reopenAfterMailTemplateDialog = (setShow) => {
    setShow(false)
    setTimeout(() => openCreateSpaceModal(), 1000 * 0.4)
  }

  // Die Vorschau zeigt die erste Adresse mit dem Namen, der schon dasteht.
  // Bei einer Adresse steht er im Formular, bei mehreren am Empfänger.
  const openMailTemplatePreview = (values) => {
    const [first, ...rest] = allRecipients(values)
    const single = !isMultiple(values)

    setMailPreview({
      open: true,
      recipient: {
        email: first?.email,
        firstname: single ? values.firstname : first?.firstname,
        lastname: single ? values.lastname : first?.lastname,
        more: rest.length
      }
    })
  }

  const closeMailTemplatePreview = () =>
    setMailPreview((current) => ({ ...current, open: false }))

  const editFromMailTemplatePreview = () => {
    closeMailTemplatePreview()
    openMailTemplateDialog(setShowEditMailTemplate)
  }

  const isLoading = state === 'IS_LOADING'

  const renderForm = ({
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    handleSubmit,
    setValues
  }) => {
    const recipients = allRecipients(values)
    const multiple = isMultiple(values)
    const sendableCount = recipients.filter(
      ({ email }, index) => !problemOf(email, index)
    ).length
    const flaggedCount = values.recipients.filter(({ email }, index) =>
      problemOf(email, index)
    ).length

    // Wechselt die Zahl der Adressen zwischen einer und mehreren, wandern
    // die Namen mit (syncRecipientNames)
    const updateRecipients = ({ recipients: chips, draft }) =>
      setValues(
        syncRecipientNames(values, {
          ...values,
          recipients: chips,
          email: draft
        })
      )

    // Enter allein sendet nicht, sonst ginge die Einladung schon nach der
    // Adresse raus, ohne Name, Stelle und Vorlage. ⌘/Strg+Enter sendet aus
    // jedem Feld. Was ein Feld selbst behandelt (offene Auswahl), bleibt dort.
    const handleFormKeyDown = (event) => {
      if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return
      if (event.defaultPrevented || event.nativeEvent?.isComposing) return

      event.preventDefault()
      handleSubmit()
    }

    // Ungültige und schon eingeladene raus, danach höchstens so viele, wie
    // pro Durchgang gehen
    const removeFlagged = () =>
      updateRecipients({
        recipients: values.recipients
          .filter(
            ({ email }) => EMAIL_REGEX.test(email) && !spaceByEmail.has(email)
          )
          .slice(0, MAX_RECIPIENTS - (values.email.trim() ? 1 : 0)),
        draft: values.email
      })

    const title =
      context === 'EMAIL'
        ? t(
            TRANSLATION_LOCATION +
              (multiple ? 'modal_title_viaemail_multi' : 'modal_title_viaemail')
          )
        : t(TRANSLATION_LOCATION + 'modal_title_vialink')

    let actionText = t(TRANSLATION_LOCATION + 'invite_action_link')
    if (context === 'EMAIL') {
      actionText = multiple
        ? t(TRANSLATION_LOCATION + 'invite_action_multi', {
            count: recipients.length
          })
        : t(TRANSLATION_LOCATION + 'invite_action')
    }

    return (
      <>
        <h2 className='text-3xl font-extrabold tracking-tight text-gray-900'>
          {title}
        </h2>
        {/* Weicher Hinweis bei vollem Talentspeicher, blockiert nicht */}
        <TalentStorageInviteHint
          className='mt-6'
          inviteCount={Math.max(1, sendableCount)}
        />
        <Form
          id={FORM_ID}
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
          noValidate
        >
          <FocusFirstError />
          {/* Link-Talente heißen in der Tabelle sonst alle
              „Einladung per Link“, deshalb fragt der Link nach dem Namen */}
          {context === 'LINK' && (
            <p className='mt-6 text-sm text-gray-500'>
              {t(TRANSLATION_LOCATION + 'name_hint_via_link')}
            </p>
          )}
          <div
            className={`${
              context === 'LINK' ? 'mt-3' : 'mt-6'
            } flex flex-col gap-y-6 lg:grid lg:grid-cols-2 lg:gap-x-4`}
          >
            {context === 'EMAIL' && (
              <div className='lg:col-span-2'>
                <EmailChipsInput
                  id='email'
                  label={t(
                    TRANSLATION_LOCATION +
                      (multiple ? 'emails_label' : 'email_label')
                  )}
                  placeholder={t(TRANSLATION_LOCATION + 'email_placeholder')}
                  description={t(TRANSLATION_LOCATION + 'email_multi_hint')}
                  recipients={values.recipients}
                  draft={values.email}
                  problemOf={(recipient, index) => {
                    const problem = problemOf(recipient.email, index)
                    return problem
                      ? t(TRANSLATION_LOCATION + CHIP_PROBLEMS[problem])
                      : null
                  }}
                  onChange={updateRecipients}
                  onBlur={handleBlur}
                  // Markierte Chips erklären sich sofort, sonst erst nach
                  // Verlassen des Felds oder dem Absenden
                  error={
                    touched.email || flaggedCount ? errors.email : undefined
                  }
                  errorAction={
                    flaggedCount > 0 && (
                      <Button.Text
                        minWidth=''
                        text={t(TRANSLATION_LOCATION + 'remove_flagged_action')}
                        onClick={removeFlagged}
                      />
                    )
                  }
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            )}
            {!multiple &&
              [
                {
                  id: 'firstname',
                  label: t(TRANSLATION_LOCATION + 'firstname_label'),
                  optional: !isFirstnameRequired,
                  autoFocus: context === 'LINK',
                  className: 'lg:col-span-1'
                },
                {
                  id: 'lastname',
                  label: t(TRANSLATION_LOCATION + 'lastname_label'),
                  optional: !isLastnameRequired,
                  className: 'lg:col-span-1'
                }
              ].map(({ className, ...input }) => (
                <div key={input.id} className={className}>
                  <Input
                    {...input}
                    value={values[input.id]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched[input.id]}
                    error={errors[input.id]}
                    autoComplete='off'
                    disabled={isLoading}
                  />
                </div>
              ))}
          </div>
          {!multiple && (isFirstnameRequired || isLastnameRequired) && (
            <p className='mt-2 text-xs text-gray-500'>
              {t(TRANSLATION_LOCATION + 'name_required_hint')}
            </p>
          )}
          {multiple && (isFirstnameRequired || isLastnameRequired) && (
            <RecipientNames problemOf={problemOf} disabled={isLoading} />
          )}
          <div className='mt-6'>
            <SelectMenu
              id='career_id'
              label={t(TRANSLATION_LOCATION + 'select_career_dropdown_label')}
              options={careerOptions}
              onChange={setCareerID}
              defaultValue={careerID}
              translate={false}
            />
          </div>
          {context === 'EMAIL' && (
            <div className='mt-6 rounded-lg bg-gray-50 px-4 py-3 ring-1 ring-inset ring-gray-200'>
              <div className='flex flex-wrap items-start justify-between gap-3'>
                <div className='min-w-0'>
                  <span className='block text-xs font-medium text-gray-500'>
                    {t(TRANSLATION_LOCATION + 'mail_template_subject_label')}
                  </span>
                  <span className='mt-0.5 block text-sm font-semibold text-gray-900'>
                    {mailTemplate.subject}
                  </span>
                </div>
                <div className='flex shrink-0 gap-x-2'>
                  <Button.SecondarySM
                    text={t(
                      TRANSLATION_LOCATION + 'preview_mail_template_action'
                    )}
                    onClick={() => openMailTemplatePreview(values)}
                  />
                  <Button.SoftSM
                    text={t(TRANSLATION_LOCATION + 'edit_mail_template_action')}
                    onClick={() =>
                      openMailTemplateDialog(setShowEditMailTemplate)
                    }
                  />
                </div>
              </div>
              <p className='mt-2 text-xs text-gray-500'>
                {t(
                  TRANSLATION_LOCATION +
                    (selectedCareer
                      ? 'mail_template_scope_career'
                      : 'mail_template_scope_default')
                )}
              </p>
              {SHOW_TALENTPOOL_LANGUAGE_SELECT && !selectedCareer && (
                <SelectMenu
                  id='language'
                  label={t(TRANSLATION_LOCATION + 'language_dropdown_label')}
                  options={getAllLanguages()}
                  onChange={setLanguage}
                  defaultValue={language}
                  className='mt-4'
                />
              )}
            </div>
          )}
          {!multiple && <TalentIdField disabled={isLoading} />}
          <div className='mt-8 flex'>
            <Button.PrimaryXL
              fullWidth
              isLoading={isLoading}
              text={actionText}
              onClick={handleSubmit}
            />
          </div>
          {isLoading && progress && (
            <p
              className='mt-3 text-center text-sm text-gray-500'
              aria-live='polite'
            >
              {t(TRANSLATION_LOCATION + 'sending_progress', progress)}
            </p>
          )}
        </Form>
      </>
    )
  }

  // „Selbst einladen“ aus dem Trial füllt Name und Adresse vor. Fehlende
  // Werte bleiben leer statt undefined.
  const prefill = locationState?.inviteYourself || {}

  const renderStep = () => {
    if (state === 'SUCCESS_BATCH') {
      return (
        <InviteBatchSuccess
          outcomes={outcomes}
          careerTitle={selectedCareer?.title}
          progress={progress}
          onRetry={retryFailed}
          onInviteAnother={() => setState('INITIAL')}
          onDone={() => handleClose({})}
        />
      )
    }

    if (isSuccess) {
      return (
        <InviteSuccess
          via={state === 'SUCCESS_VIA_EMAIL' ? 'EMAIL' : 'LINK'}
          space={space}
          invitee={invitee}
          careerTitle={selectedCareer?.title}
          mailNotSent={mailNotSent}
          isResending={isResending}
          onResend={resendMail}
          onInviteAnother={() => setState('INITIAL')}
          onDone={() => handleClose({})}
        />
      )
    }

    return (
      <Formik
        innerRef={formikRef}
        enableReinitialize
        validateOnMount
        initialValues={
          draftValues.current || {
            ...INITIAL_VALUES,
            email: prefill.email || '',
            firstname: prefill.firstname || '',
            lastname: prefill.lastname || ''
          }
        }
        onSubmit={(values, { setFieldValue, resetForm }) =>
          inviteTalent({ values, setFieldValue, resetForm })
        }
        validationSchema={validationSchema}
      >
        {renderForm}
      </Formik>
    )
  }

  return (
    <>
      <Modal open={open} setOpen={() => handleClose({})} size='lg'>
        <StepTransition stepKey={isSuccess ? state : 'FORM'}>
          {renderStep()}
        </StepTransition>
        {/* Im Modal, damit HeadlessUI die Vorschau als verschachtelten Dialog
            kennt und das Einladen-Modal beim Klick darin offen bleibt */}
        <EditMailTemplate
          open={mailPreview.open}
          setOpen={closeMailTemplatePreview}
          mailTemplate={mailTemplate}
          mailTemplateMacros={mailTemplateMacros}
          recipient={mailPreview.recipient}
          onEdit={editFromMailTemplatePreview}
          preview
        />
      </Modal>
      <EditMailTemplate
        open={showEditMailTemplate}
        setOpen={() => reopenAfterMailTemplateDialog(setShowEditMailTemplate)}
        {...editMailTemplateProps}
      />
    </>
  )
}

CreateSpaceModal.propTypes = {
  data: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  closeCreateSpaceModal: PropTypes.func.isRequired,
  openCreateSpaceModal: PropTypes.func.isRequired,
  refetchSpaces: PropTypes.func.isRequired,
  invitationType: PropTypes.string,
  // bekommt beim Schließen die IDs aller in dieser Sitzung eingeladenen Talente
  onInvited: PropTypes.func
}

CreateSpaceModal.defaultProps = {
  invitationType: null,
  onInvited: () => {}
}

export default CreateSpaceModal
